import React, { useEffect, useState } from "react";
import axios from "axios";
import { Domain } from "Domain";
import DoctorsTable from "components/Table/DoctorsTable.js";
import CardBody from "components/Card/CardBody.js";
import CardFooter from "components/Card/CardFooter.js";
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import Button from "components/CustomButtons/Button.js";
import Snackbar from "components/Snackbar/Snackbar.js";
import AddAlert from "@material-ui/icons/AddAlert";
import Danger from "components/Typography/Danger";
import EmptyTable from "components/EmptyTable";
import SingleSelect from "components/SingleSelect";
import LinearIndeterminate from "components/Loader/LinearLoader";
import LoadingOverlay from "react-loading-overlay";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import MuiButton from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import { Chip } from "@material-ui/core";
import Slide from "@mui/material/Slide";
import { saveAs } from "file-saver";
import XlsxPopulate from "xlsx-populate";
import * as xlsx from "xlsx";
import {
  ChartLineUp,
  DownloadSimple,
  PencilSimple,
  Plus,
  UploadSimple,
  UsersThree,
  X,
} from "@phosphor-icons/react";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const modalityColors = {
  CT: { bg: "#e8f0fe", color: "#2f72f2" },
  MR: { bg: "#ecfdf3", color: "#16833a" },
  DX: { bg: "#fff7e6", color: "#a05a00" },
};

function DashboardStat({ label, value, caption }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        minHeight: 104,
        borderRadius: "8px",
        border: "1px solid #e8eef7",
        background: "#ffffff",
        boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)",
        transition: "transform 180ms ease, box-shadow 180ms ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 22px 46px rgba(15, 23, 42, 0.09)",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: "8px",
            background: "rgba(47, 114, 242, 0.12)",
            display: "grid",
            placeItems: "center",
            color: "#2f72f2",
          }}
        >
          <UsersThree size={16} weight="duotone" />
        </Box>
        <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 800 }}>
          {label}
        </Typography>
      </Box>
      <Typography
        sx={{ mt: 1.1, color: "#162033", fontSize: 25, fontWeight: 900 }}
      >
        {value}
      </Typography>
      <Typography sx={{ color: "#94a3b8", fontSize: 12 }}>{caption}</Typography>
    </Paper>
  );
}

function DashboardPulse({ total, label }) {
  const bars = [38, 52, 68, 46, 74, 86, 62];
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: "8px",
        border: "1px solid #e8eef7",
        background: "#ffffff",
        boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1.5 }}>
        <Box>
          <Typography sx={{ color: "#162033", fontSize: 16, fontWeight: 900 }}>
            Registry activity
          </Typography>
          <Typography sx={{ color: "#94a3b8", fontSize: 12 }}>
            {label} tariff momentum
          </Typography>
        </Box>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: "8px",
            display: "grid",
            placeItems: "center",
            color: "#2f72f2",
            background: "#e8f0fe",
          }}
        >
          <ChartLineUp size={18} weight="duotone" />
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "end", gap: 0.8, height: 92 }}>
        {bars.map((height, index) => (
          <Box
            key={index}
            sx={{
              flex: 1,
              height: `${height}%`,
              borderRadius: "8px 8px 0 0",
              background: index === 4 ? "#2f72f2" : "#8fc1ff",
              animation: "thsBarRise 700ms ease both",
              animationDelay: `${index * 70}ms`,
            }}
          />
        ))}
      </Box>
      <Typography sx={{ mt: 1.4, color: "#64748b", fontSize: 12 }}>
        {total} active records tracked in the billing registry.
      </Typography>
    </Paper>
  );
}

function BodyPartsPricingTable({ rows, onEdit, onModalityFilter }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const paginatedRows = React.useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [rows]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "8px",
        border: "1px solid #e8eef7",
        overflow: "hidden",
        boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)",
      }}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {["Body part", "Modality", "Price", "Status", ""].map((head) => (
                <TableCell
                  key={head}
                  sx={{
                    borderBottom: "1px solid #e8eef7",
                    color: "#64748b",
                    fontSize: 12,
                    fontWeight: 800,
                    background: "#f8fbff",
                  }}
                >
                  {head}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5, border: 0 }}>
                  <Typography sx={{ color: "#94a3b8", fontSize: 14 }}>
                    No body-part pricing configured
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row) => {
                const tone = modalityColors[row.Modality] || modalityColors.CT;
                return (
                  <TableRow key={row.Id} hover>
                    <TableCell sx={{ borderBottom: "1px solid #eef3f9" }}>
                      <Typography
                        sx={{
                          color: "#162033",
                          fontSize: 14,
                          fontWeight: 800,
                        }}
                      >
                        {row.BodyPart}
                      </Typography>
                      <Typography sx={{ color: "#94a3b8", fontSize: 12 }}>
                        ID #{row.Id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #eef3f9" }}>
                      <Chip
                        size="small"
                        label={row.Modality}
                        onClick={() => onModalityFilter(row.Modality)}
                        style={{
                          background: tone.bg,
                          color: tone.color,
                          fontWeight: 800,
                          borderRadius: 8,
                          cursor: "pointer",
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #eef3f9" }}>
                      <Typography
                        sx={{
                          color: "#162033",
                          fontSize: 14,
                          fontWeight: 900,
                        }}
                      >
                        Rs. {row.Price}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: "1px solid #eef3f9" }}>
                      <Chip
                        size="small"
                        label={row.Status || "Active"}
                        style={{
                          background: "#ecfdf3",
                          color: "#16833a",
                          fontWeight: 800,
                          borderRadius: 8,
                        }}
                      />
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ borderBottom: "1px solid #eef3f9" }}
                    >
                      <Tooltip title="Edit pricing">
                        <IconButton
                          size="small"
                          onClick={() => onEdit(row.Id)}
                          sx={{
                            color: "#2f72f2",
                            background: "#e8f0fe",
                            borderRadius: "8px",
                            "&:hover": { background: "#d8e7ff" },
                          }}
                        >
                          <PencilSimple size={18} weight="duotone" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {rows.length > 0 && (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          sx={{ borderTop: "1px solid #e8eef7" }}
        />
      )}
    </Paper>
  );
}

export default function ClientsList() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
  }, []);

  const [saved, setSaved] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [edit, setEdit] = useState("");
  const [clients, setClients] = useState([]);
  const [validated, setValidated] = useState(true);
  const [bodyPartValidated, setBodyPartValidated] = useState(true);
  const [loading, setLoading] = useState(true);
  const [empty, setEmpty] = useState(false);
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [bodyParts, setBodyParts] = useState([]);
  const [clientDetails, setClientDetails] = useState({});
  const [bodyPartsLoading, setBodyPartsLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [priceDialogOpen, setPriceDialogOpen] = useState(false);
  const [addBodyPart, setAddBodyPart] = useState(false);
  const [bodyPartEdit, setBodyPartEdit] = useState("");
  const [selected, setSelected] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [selectedBodyPart, setSelectedBodyPart] = useState("");
  const [jsonToInsert, setJsonToInsert] = useState([]);
  const [data, setData] = useState({ Id: 0, FullName: "" });
  const [bodypartData, setBodypartData] = useState({
    Id: 0,
    BodyPart: "",
    Price: "",
    Modality: "",
    ModalityId: "",
    ClientId: "",
    Status: "Active",
  });
  const [modalityValues, setModalityValues] = useState({
    Id: null,
    label: "",
  });

  const showSavedNotification = () => {
    if (!saved) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const showDeletedNotification = () => {
    if (!deleted) {
      setDeleted(true);
      setTimeout(() => setDeleted(false), 3000);
    }
  };

  const isSuccessResponse = (response) => {
    if (response.data && typeof response.data === "object") {
      return response.data.success;
    }
    return (
      typeof response.data === "string" &&
      response.data.includes('"success":true')
    );
  };

  const resetBodyPartForm = () => {
    setBodypartData({
      Id: 0,
      BodyPart: "",
      Price: "",
      Modality: "",
      ModalityId: "",
      ClientId: "",
      Status: "Active",
    });
    setBodyPartEdit("");
    setSelected("");
    setPriceDialogOpen(false);
    setAddBodyPart(false);
  };

  function fetchClientDetails(id) {
    setBodyPartsLoading(true);
    let formData = new FormData();
    formData.append("Id", id);
    axios({
      method: "post",
      url: Domain + "/GetClientById.php",
      data: formData,
      config: { headers: { "Content-Type": "multipart/form-data" } },
    })
      .then((response) => {
        if (response.data.success)
          setClientDetails(response.data.data[0] || {});
        let bodyFormData = new FormData();
        bodyFormData.append("Id", id);
        return axios({
          method: "post",
          url: Domain + "/GetBodypartsByClientId.php",
          data: bodyFormData,
          config: { headers: { "Content-Type": "multipart/form-data" } },
        });
      })
      .then((response) => {
        setBodyParts(
          response.data.success && response.data.TotalCount > 0
            ? response.data.data
            : []
        );
        setBodyPartsLoading(false);
      })
      .catch((response) => {
        setBodyPartsLoading(false);
        console.log(response);
      });
  }

  useEffect(() => {
    let formData = new FormData();
    formData.append("Status", "Active");
    setLoading(true);
    axios({
      method: "post",
      url: Domain + "/GetAllClients.php",
      data: formData,
      config: { headers: { "Content-Type": "multipart/form-data" } },
    })
      .then((response) => {
        if (response.data.TotalCount != 0) {
          setClients(response.data.data);
          setEmpty(false);
        } else {
          setEmpty(true);
        }
        setLoading(false);
      })
      .catch((response) => {
        showDeletedNotification();
        setLoading(false);
        console.log(response);
      });
  }, [refresh]);

  useEffect(() => {
    if (edit !== "") {
      setDetailsOpen(true);
      fetchClientDetails(edit);
    }
  }, [edit]);

  useEffect(() => {
    if (selectedBodyPart && selectedBodyPart.Id) {
      setBodyPartEdit(selectedBodyPart.Id);
    }
  }, [selectedBodyPart]);

  useEffect(() => {
    if (bodyPartEdit !== "") {
      setEditLoading(true);
      let formData = new FormData();
      formData.append("Id", bodyPartEdit);
      axios({
        method: "post",
        url: Domain + "/GetBodypartsByIdClient.php",
        data: formData,
        config: { headers: { "Content-Type": "multipart/form-data" } },
      })
        .then((response) => {
          if (response.data.success && response.data.TotalCount > 0) {
            const item = response.data.data[0];
            setBodypartData(item);
            setSelected({ value: item.ModalityId, label: item.Modality });
            setModalityValues({ Id: item.ModalityId, Label: item.Modality });
            setPriceDialogOpen(true);
          }
          setEditLoading(false);
        })
        .catch((response) => {
          setEditLoading(false);
          console.log(response);
        });
    }
  }, [bodyPartEdit]);

  function handleBack() {
    setDetailsOpen(false);
    setEdit("");
    setClientDetails({});
    setBodyParts([]);
    resetBodyPartForm();
  }

  function handleData(e) {
    setData({ ...data, [e.target.id]: e.target.value });
  }

  function handleBodyData(e) {
    setBodypartData({ ...bodypartData, [e.target.id]: e.target.value });
  }

  function handleClear() {
    setData({ Id: 0, FullName: "" });
  }

  function handleSave() {
    if (data.FullName === "") {
      setValidated(false);
      return;
    }
    setValidated(true);
    let formData = new FormData();
    formData.append("Id", data.Id);
    formData.append("FullName", data.FullName);
    formData.append(
      "StringId",
      data.FullName.replace(/ /g, "")
        .replace(/[^a-zA-Z0-9 ]/g, "")
        .toLowerCase()
    );
    axios({
      method: "post",
      url: Domain + "/SaveClient.php",
      data: formData,
      config: { headers: { "Content-Type": "multipart/form-data" } },
    })
      .then((response) => {
        if (response.data.success) {
          let defaultData = new FormData();
          defaultData.append("Id", response.data.data.Id);
          return axios({
            method: "post",
            url: Domain + "/SaveClientDefaultData.php",
            data: defaultData,
            config: { headers: { "Content-Type": "multipart/form-data" } },
          });
        }
        throw response;
      })
      .then((response) => {
        if (isSuccessResponse(response)) {
          handleClear();
          setAddClientOpen(false);
          setEmpty(false);
          showSavedNotification();
          setRefresh((value) => !value);
        } else {
          showDeletedNotification();
        }
      })
      .catch((response) => {
        setDeleted(true);
        console.log(response);
      });
  }

  function handleBodyPartSave() {
    if (
      bodypartData.BodyPart === "" ||
      !(modalityValues.Label || modalityValues.label) ||
      bodypartData.Price === ""
    ) {
      setBodyPartValidated(false);
      return;
    }
    setBodyPartValidated(true);
    let formData = new FormData();
    formData.append("Id", bodypartData.Id);
    formData.append("BodyPart", bodypartData.BodyPart);
    formData.append("Price", bodypartData.Price);
    formData.append("Modality", modalityValues.Label || modalityValues.label);
    formData.append("ModalityId", modalityValues.Id || modalityValues.value);
    formData.append("ClientId", edit);
    formData.append(
      "StringId",
      bodypartData.BodyPart.replace(/ /g, "")
        .replace(/[^a-zA-Z0-9 ]/g, "")
        .toLowerCase()
    );
    axios({
      method: "post",
      url: Domain + "/saveBodyPartsClient.php",
      data: formData,
      config: { headers: { "Content-Type": "multipart/form-data" } },
    })
      .then((response) => {
        if (response.data.success) {
          resetBodyPartForm();
          fetchClientDetails(edit);
          showSavedNotification();
        } else {
          showDeletedNotification();
        }
      })
      .catch((response) => {
        setDeleted(true);
        console.log(response);
      });
  }

  function handleModalityFilter(modality, id) {
    let formData = new FormData();
    formData.append("Id", id);
    formData.append("Modality", modality);
    axios({
      method: "post",
      url: Domain + "/GetBodypartsByClientIdAndModality.php",
      data: formData,
      config: { headers: { "Content-Type": "multipart/form-data" } },
    })
      .then((response) => {
        if (response.data.TotalCount != 0 && response.data.success) {
          setBodyParts(response.data.data);
        }
      })
      .catch((response) => {
        showDeletedNotification();
        console.log(response);
      });
  }

  const readUploadFile = (e) => {
    e.preventDefault();
    if (e.target.files) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const workbook = xlsx.read(event.target.result, { type: "array" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        setJsonToInsert(xlsx.utils.sheet_to_json(worksheet));
      };
      reader.readAsArrayBuffer(e.target.files[0]);
    }
  };

  function getSheetData(rows, header) {
    const fields = Object.keys(rows[0]);
    const sheetData = rows.map((row) =>
      fields.map((fieldName) => row[fieldName] || "")
    );
    sheetData.unshift(header);
    return sheetData;
  }

  async function saveAsExcelClient() {
    if (bodyParts.length > 0) {
      const header = [
        "Id",
        "BodyPart",
        "Price",
        "Modality",
        "ModalityId",
        "Status",
        "ClientId",
      ];
      XlsxPopulate.fromBlankAsync().then(async (workbook) => {
        const sheet1 = workbook.sheet(0);
        const sheetData = getSheetData(bodyParts, header);
        const totalColumns = sheetData[0].length;
        sheet1.cell("A1").value(sheetData);
        ["A", "B", "C", "D", "E", "F", "G"].forEach((column) =>
          sheet1.column(column).width(25)
        );
        const range = sheet1.usedRange();
        const endColumn = String.fromCharCode(64 + totalColumns);
        sheet1.row(1).style("bold", true);
        sheet1.range("A1:" + endColumn + "1").style("fill", "d3d3d3");
        range.style("border", false);
        return workbook
          .outputAsync()
          .then((res) => saveAs(res, "bodyparts_client.xlsx"));
      });
    }
  }

  function handleUploadBulkData() {
    if (jsonToInsert.length > 0) {
      let formData = new FormData();
      formData.append("Id", edit);
      axios({
        method: "post",
        url: Domain + "/DeleteBodyPartsByClientId.php",
        data: formData,
        config: { headers: { "Content-Type": "multipart/form-data" } },
      })
        .then((response) => {
          if (response.data.success) {
            return axios({
              method: "post",
              url: Domain + "/SaveBodyPartsFromExcel.php",
              data: JSON.stringify(jsonToInsert),
              config: { headers: { "Content-Type": "multipart/form-data" } },
            });
          }
          throw response;
        })
        .then(() => {
          fetchClientDetails(edit);
          showSavedNotification();
        })
        .catch((response) => console.log(response));
    } else {
      alert("Select excel file to upload!");
    }
  }

  const clientsData = clients.map((client) => ({
    Id: client.Id,
    Name: client.Name,
  }));
  const bodyPartSelect = bodyParts.map((item) => ({
    Id: item.Id,
    value: item.Id,
    label: item.BodyPart,
  }));
  const modalityList = [
    { value: 1, label: "CT" },
    { value: 2, label: "MR" },
    { value: 3, label: "DX" },
  ];
  const configuredBodyParts = bodyParts.length;
  const uniqueModalities = new Set(bodyParts.map((item) => item.Modality)).size;

  return (
    <Box>
      <Snackbar
        place="bc"
        color="success"
        icon={AddAlert}
        message="Client saved successfully"
        open={saved}
        closeNotification={() => setSaved(false)}
        close
      />
      <Snackbar
        place="bc"
        color="danger"
        icon={AddAlert}
        message="Failed. Try again"
        open={deleted}
        closeNotification={() => setDeleted(false)}
        close
      />
      <Dialog
        open={addClientOpen}
        onClose={() => setAddClientOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: "1px solid #eef1f5",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: 18, fontWeight: 800, color: "#162033" }}
            >
              Add Client
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: 0.25 }}>
              Enter the client details
            </Typography>
          </Box>
          <IconButton onClick={() => setAddClientOpen(false)}>
            <X size={22} weight="bold" />
          </IconButton>
        </Box>
        <form>
          <CardBody>
            <GridContainer>
              <GridItem xs={12} sm={12} md={12}>
                <CustomInput
                  onChange={handleData}
                  value={data.FullName}
                  labelText="Name"
                  id="FullName"
                  formControlProps={{ fullWidth: true }}
                />
              </GridItem>
            </GridContainer>
            {!validated && <Danger>Please enter Client Name to save</Danger>}
          </CardBody>
          <CardFooter>
            <Button onClick={() => setAddClientOpen(false)} color="white">
              Cancel
            </Button>
            <Button onClick={handleClear} color="white">
              Clear
            </Button>
            <Button onClick={handleSave} color="primary">
              Save
            </Button>
          </CardFooter>
        </form>
      </Dialog>
      <Dialog
        open={detailsOpen}
        onClose={handleBack}
        fullWidth
        maxWidth="lg"
        TransitionComponent={Transition}
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            borderBottom: "1px solid #e8eef7",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: 22, fontWeight: 900, color: "#162033" }}
            >
              {clientDetails.Name || "Client details"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#7b8798" }}>
              Client tariff configuration and body-part pricing
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <MuiButton
              variant="contained"
              startIcon={<Plus size={17} weight="bold" />}
              onClick={() => setAddBodyPart(true)}
              sx={{ boxShadow: "none", fontWeight: 800 }}
            >
              Add Price
            </MuiButton>
            <IconButton onClick={handleBack}>
              <X size={22} weight="bold" />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ p: { xs: 2, md: 3 }, background: "#fbfdff" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: 2,
              mb: 2.5,
            }}
          >
            <DashboardStat
              label="Body Parts"
              value={configuredBodyParts}
              caption="Configured for this client"
            />
            <DashboardStat
              label="Modalities"
              value={uniqueModalities || 0}
              caption="CT, MR and DX coverage"
            />
            <DashboardStat
              label="Status"
              value="Active"
              caption="Available for billing"
            />
          </Box>
          {addBodyPart && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                mb: 2.5,
                borderRadius: "8px",
                border: "1px solid #e8eef7",
              }}
            >
              <Typography
                sx={{ color: "#162033", fontSize: 16, fontWeight: 900, mb: 1 }}
              >
                Add body-part pricing
              </Typography>
              <CardBody>
                <GridContainer>
                  <GridItem xs={12} sm={12} md={4}>
                    <CustomInput
                      onChange={handleBodyData}
                      value={bodypartData.BodyPart}
                      labelText="Body Part"
                      id="BodyPart"
                      formControlProps={{ fullWidth: true }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={4}>
                    <CustomInput
                      onChange={handleBodyData}
                      value={bodypartData.Price}
                      labelText="Price"
                      id="Price"
                      formControlProps={{ fullWidth: true }}
                    />
                  </GridItem>
                  <GridItem xs={12} sm={12} md={4}>
                    <SingleSelect
                      noOptionsMessage="No modalities"
                      placeholder="Select Modality"
                      Options={modalityList}
                      setValue={setModalityValues}
                      formControlProps={{ fullWidth: true }}
                    />
                  </GridItem>
                  {!bodyPartValidated && <Danger>Input all the details</Danger>}
                </GridContainer>
              </CardBody>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button onClick={resetBodyPartForm} color="white">
                  Cancel
                </Button>
                <Button onClick={handleBodyPartSave} color="primary">
                  Save
                </Button>
              </Box>
            </Paper>
          )}
          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 2.5,
              borderRadius: "8px",
              border: "1px solid #e8eef7",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Box>
              <Typography
                sx={{ color: "#162033", fontSize: 14, fontWeight: 900 }}
              >
                Tariff data
              </Typography>
              <Typography sx={{ color: "#94a3b8", fontSize: 12 }}>
                Import or export client-specific body-part prices
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
              }}
            >
              <form>
                <input
                  type="file"
                  name="upload"
                  id="client-upload"
                  onChange={readUploadFile}
                  style={{ display: "none" }}
                />
                <MuiButton
                  component="label"
                  htmlFor="client-upload"
                  variant="outlined"
                  startIcon={<UploadSimple size={18} weight="duotone" />}
                  sx={{ fontWeight: 800 }}
                >
                  Choose file
                </MuiButton>
              </form>
              <MuiButton
                onClick={saveAsExcelClient}
                variant="outlined"
                startIcon={<DownloadSimple size={18} weight="duotone" />}
                sx={{ fontWeight: 800 }}
              >
                Export
              </MuiButton>
              <MuiButton
                onClick={handleUploadBulkData}
                variant="contained"
                sx={{ fontWeight: 800, boxShadow: "none" }}
              >
                Upload
              </MuiButton>
            </Box>
          </Paper>
          {bodyPartsLoading ? (
            <LinearIndeterminate />
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "180px 1fr" },
                  gap: 1.5,
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <MuiButton
                  onClick={() => fetchClientDetails(edit)}
                  variant="outlined"
                  sx={{ fontWeight: 800 }}
                >
                  Clear filter
                </MuiButton>
                <SingleSelect
                  noOptionsMessage="No bodyParts"
                  placeholder="Search body part"
                  Options={bodyPartSelect}
                  setValue={setSelectedBodyPart}
                  formControlProps={{ fullWidth: true }}
                />
              </Box>
              <LoadingOverlay active={editLoading} spinner text="Please Wait..">
                <BodyPartsPricingTable
                  rows={bodyParts}
                  onEdit={setBodyPartEdit}
                  onModalityFilter={(modality) =>
                    handleModalityFilter(modality, edit)
                  }
                />
              </LoadingOverlay>
            </>
          )}
        </Box>
      </Dialog>
      <Dialog
        open={priceDialogOpen}
        onClose={resetBodyPartForm}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <Box
          sx={{
            p: 2.5,
            borderBottom: "1px solid #e8eef7",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ color: "#162033", fontSize: 18, fontWeight: 900 }}>
            Edit body-part pricing
          </Typography>
          <IconButton onClick={resetBodyPartForm}>
            <X size={22} weight="bold" />
          </IconButton>
        </Box>
        <Box sx={{ p: 2.5 }}>
          <CardBody>
            <GridContainer>
              <GridItem xs={12} sm={12} md={4}>
                <CustomInput
                  onChange={handleBodyData}
                  value={bodypartData.BodyPart}
                  labelText="Body Part"
                  id="BodyPart"
                  formControlProps={{ fullWidth: true }}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={4}>
                <CustomInput
                  onChange={handleBodyData}
                  value={bodypartData.Price}
                  labelText="Price"
                  id="Price"
                  formControlProps={{ fullWidth: true }}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={4}>
                <SingleSelect
                  selectedVal={selected}
                  noOptionsMessage="No modalities"
                  placeholder="Select Modality"
                  Options={modalityList}
                  setValue={setModalityValues}
                  formControlProps={{ fullWidth: true }}
                />
              </GridItem>
              {!bodyPartValidated && <Danger>Input all the details</Danger>}
            </GridContainer>
          </CardBody>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button onClick={resetBodyPartForm} color="white">
              Cancel
            </Button>
            <Button onClick={handleBodyPartSave} color="primary">
              Save
            </Button>
          </Box>
        </Box>
      </Dialog>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 28, fontWeight: 900, color: "#162033" }}>
            Client Registry
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#64748b", mt: 0.6 }}>
            Manage client master data and tariff configuration
          </Typography>
        </Box>
        <MuiButton
          variant="contained"
          startIcon={<Plus size={17} weight="bold" />}
          onClick={() => setAddClientOpen(true)}
          sx={{
            background: "#2f72f2",
            fontWeight: 900,
            boxShadow: "0 8px 18px rgba(47,114,242,0.22)",
            px: 2,
            py: 1,
            "&:hover": { background: "#1d5fce" },
          }}
        >
          Add Client
        </MuiButton>
      </Box>
      <Box sx={{ mb: 2 }}>
        <DashboardPulse total={clients.length} label="Client" />
      </Box>
      <Paper
        elevation={0}
        sx={{
          borderRadius: "8px",
          p: 1.5,
          border: "1px solid #e8eef7",
          background: "#ffffff",
          boxShadow: "0 14px 34px rgba(15, 23, 42, 0.06)",
        }}
      >
        <Box
          sx={{
            px: 1.5,
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: 20, color: "#162033", fontWeight: 900 }}
            >
              Client registry
            </Typography>
            <Typography sx={{ fontSize: 14, color: "#94a3b8", mt: 0.4 }}>
              Select a client to edit body-part prices
            </Typography>
          </Box>
          <Chip
            label="Production"
            size="small"
            style={{ background: "#ecfdf3", color: "#16833a", fontWeight: 800 }}
          />
        </Box>
        <Divider sx={{ mb: 1.5 }} />
        {empty ? (
          <EmptyTable />
        ) : (
          <DoctorsTable
            data={clientsData}
            loading={loading}
            onEdit={setEdit}
            entityLabel="client"
            searchPlaceholder="Search clients..."
            actionTooltip="Open client pricing"
          />
        )}
      </Paper>
    </Box>
  );
}
